import { useCallback, useEffect, useRef } from 'react';

import { logger } from 'client/utils/logger/logger';
import { EventToolbox } from 'client/utils/event-toolbox';
import { sendMarketoEmailReport } from 'client/data/utils/marketo-service';
import { collectAppraisalEmailValues } from 'site-modules/shared/utils/appraiser/email-utils';
import { EMAIL_REPORT_STATUSES } from 'site-modules/shared/constants/appraisal/appraisal';
import { APPRAISAL_EMAIL_STEP_SEND_EMAIL } from 'site-modules/shared/constants/appraisal/events';
import { SMART_SIGN_VALID_EMAIL_EVENT } from 'site-modules/shared/components/profile/smart-sign/helper';
import { getEmailReportConditions } from 'site-modules/shared/components/appraisal/trade-in-appraiser/utils/trade-in-appraiser-utils';

/**
 * Manages the email report flow: sending the email, resetting status,
 * and wiring up the global EventToolbox listeners.
 *
 * @param {Object} params
 * @param {Object} params.stateRef - ref always pointing at the current state (avoids state in dep arrays)
 * @param {Function} params.setState - state setter
 * @param {string} params.userEmail - pre-filled user email from profile
 */
export function useEmailReport({ stateRef, setState, userEmail }) {
  // Stable ref so event listeners registered once always call the latest emailReport closure.
  const emailReportRef = useRef(null);

  const resetEmailReportStatus = useCallback(() => {
    setState(prev => ({ ...prev, emailReportStatus: '' }));
  }, [setState]);

  const emailReport = useCallback(
    async ({ email, isEmailStep = false }) => {
      const { mileage, exteriorColor, condition, zipCode, vehicle, appraisalEmailData, partnerOfferEmailData } =
        stateRef.current;
      const { hasEstimatedValues, hasValuation } = getEmailReportConditions(partnerOfferEmailData);
      const emailToSend = email || userEmail;

      try {
        setState(prev => ({ ...prev, emailReportStatus: EMAIL_REPORT_STATUSES.IN_PROGRESS }));

        const shouldSendEmail = isEmailStep ? !hasValuation && !hasEstimatedValues : true;

        if (shouldSendEmail && appraisalEmailData?.isReportAvailable) {
          await sendMarketoEmailReport({
            data: collectAppraisalEmailValues({
              mileage,
              exteriorColor,
              condition,
              zipCode,
              vehicle,
              appraisalEmailData,
              partnerOfferEmailData,
            }),
            campaignName: 'appraisal_email',
            email: emailToSend,
          });
        }

        setState(prev => ({ ...prev, emailReportStatus: EMAIL_REPORT_STATUSES.SENT }));
      } catch (e) {
        setState(prev => ({ ...prev, emailReportStatus: EMAIL_REPORT_STATUSES.SENT }));
        logger.warn("Appraisal email wasn't sent properly", e);
      }
    },
    [setState, userEmail, stateRef]
  );

  // Keep ref current on every render so stable event handlers always call the latest closure.
  // eslint-disable-next-line react-hooks/refs -- stable custom-event handlers require the latest callback.
  emailReportRef.current = emailReport;

  const handleCustomEventEmailReport = useCallback(({ detail }) => {
    emailReportRef.current({ email: detail.email });
  }, []);

  const handleSendEmailStepEmail = useCallback(({ detail }) => {
    emailReportRef.current({ email: detail.email, isEmailStep: true });
  }, []);

  // Register / deregister global event listeners once (mount / unmount).
  useEffect(() => {
    EventToolbox.on(SMART_SIGN_VALID_EMAIL_EVENT, handleCustomEventEmailReport);
    EventToolbox.on(APPRAISAL_EMAIL_STEP_SEND_EMAIL, handleSendEmailStepEmail);
    return () => {
      EventToolbox.off(SMART_SIGN_VALID_EMAIL_EVENT, handleCustomEventEmailReport);
      EventToolbox.off(APPRAISAL_EMAIL_STEP_SEND_EMAIL, handleSendEmailStepEmail);
    };
  }, [handleCustomEventEmailReport, handleSendEmailStepEmail]);

  return {
    emailReport,
    resetEmailReportStatus,
    handleCustomEventEmailReport,
    handleSendEmailStepEmail,
  };
}
